import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TreeBrowser from '../Tree/TreeBrowser';
import OfficeSettingsModal from '../Settings/OfficeSettingsModal';
import { useAuth } from '../../contexts/AuthContext';
import { useBranchFilter } from '../../contexts/BranchFilterContext';
import { branchAPI } from '../../services/api';
import './Layout.css';

// Browsing mode: the Finder-style tree, or the classic sidebar menu. They are
// alternatives, never shown together, and the choice survives a reload.
const NAV_MODE_KEY = 'navMode';
const readNavMode = () => (localStorage.getItem(NAV_MODE_KEY) === 'menu' ? 'menu' : 'tree');

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout, isSuperAdmin, isOrgAdmin } = useAuth();
  const { selectedBranchId, setSelectedBranchId } = useBranchFilter();
  const [expandedSections, setExpandedSections] = useState({
    agarModules: false,
    agarConfiguration: false,
    systemConfiguration: false,
    incomeAndExpenses: false,
    forms: false,
  });
  const [showAvatarDropdown, setShowAvatarDropdown] = useState(false);
  const [branches, setBranches] = useState([]);
  const [showBranchSelector, setShowBranchSelector] = useState(false);
  const [navMode, setNavMode] = useState(readNavMode);
  const [showOfficeSettings, setShowOfficeSettings] = useState(false);
  const [branchSearchTerm, setBranchSearchTerm] = useState('');
  const branchSelectRef = React.useRef(null);

  // Ensure RTL is applied when language changes
  useEffect(() => {
    const currentLang = i18n.language || localStorage.getItem('language') || 'en';
    const isRTL = currentLang === 'ar';
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', currentLang);
    if (document.body) {
      document.body.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    }
  }, [i18n.language]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    // Update HTML and body direction for RTL support
    const isRTL = lng === 'ar';
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lng);
    if (document.body) {
      document.body.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    }
    setShowAvatarDropdown(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    setShowAvatarDropdown(false);
  };

  // Load branches on mount - filter by organization for admin users, or show only user's branch for branch users
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await branchAPI.getAll('', 1, 1000);
        let allBranches = [];
        if (response.data && response.data.items) {
          allBranches = response.data.items;
        } else if (Array.isArray(response.data)) {
          allBranches = response.data;
        }
        
        // Filter branches for branch users - only show their branch
        if (user && (user.branchId || user.BranchId)) {
          const userBranchId = user.branchId || user.BranchId;
          const filteredBranches = allBranches.filter(branch => {
            const branchId = branch.id || branch.Id;
            return branchId != null && Number(branchId) === Number(userBranchId);
          });
          setBranches(filteredBranches);
          // Auto-select the user's branch
          if (filteredBranches.length > 0 && !selectedBranchId) {
            setSelectedBranchId(filteredBranches[0].id || filteredBranches[0].Id);
          }
        }
        // Filter branches for admin users - only show branches from their organization
        else if (!isSuperAdmin && user) {
          const userOrgId = user.organizationId || user.OrganizationId;
          if (userOrgId) {
            const filteredBranches = allBranches.filter(branch => {
              const branchOrgId = branch.organizationId || branch.OrganizationId;
              // Compare as numbers to handle type mismatches
              return branchOrgId != null && Number(branchOrgId) === Number(userOrgId);
            });
            setBranches(filteredBranches);
          } else {
            // Admin user without organizationId - show no branches
            setBranches([]);
          }
        } else {
          // Super admin sees all branches
          setBranches(allBranches);
        }
      } catch (error) {
        console.error('Error loading branches:', error);
      }
    };
    loadBranches();
  }, [isSuperAdmin, user?.organizationId, user?.branchId, user?.BranchId, selectedBranchId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAvatarDropdown && !event.target.closest('.avatar-dropdown-container')) {
        setShowAvatarDropdown(false);
      }
      if (showBranchSelector && branchSelectRef.current && !branchSelectRef.current.contains(event.target)) {
        setShowBranchSelector(false);
        setBranchSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAvatarDropdown, showBranchSelector]);

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const isCurrentlyExpanded = prev[section];
      // If clicking to expand, close all others. If clicking to collapse, just collapse this one.
      return {
        agarModules: section === 'agarModules' ? !isCurrentlyExpanded : false,
        agarConfiguration: section === 'agarConfiguration' ? !isCurrentlyExpanded : false,
        systemConfiguration: section === 'systemConfiguration' ? !isCurrentlyExpanded : false,
        incomeAndExpenses: section === 'incomeAndExpenses' ? !isCurrentlyExpanded : false,
        forms: section === 'forms' ? !isCurrentlyExpanded : false,
      };
    });
  };

  // Function to get navbar title and back button info based on route
  const getNavbarInfo = () => {
    const path = location.pathname;
    
    // Special case for property select
    if (path === '/property/select') {
      const returnUrl = location.state?.returnUrl || '/contract/create';
      return {
        showNavbar: true,
        title: t('property.select.title'),
        backPath: returnUrl,
        backText: t('property.select.backToContract'),
      };
    }
    
    // Map for list routes: route => { entityKey, icon }
    const listRoutesMap = {
      '/property': { entityKey: 'property', icon: 'fa-home' },
      '/contract': { entityKey: 'contract', icon: 'fa-file-contract' },
      '/brokerage-agreement': { entityKey: 'brokerageAgreement', icon: 'fa-handshake' },
      '/brokerage': { entityKey: 'brokerage', icon: 'fa-handshake' },
      '/property-management-income': { entityKey: 'propertyManagementIncome', icon: 'fa-chart-line' },
      '/additional-income-report': { entityKey: 'additionalIncomeReport', icon: 'fa-file-invoice-dollar' },
      '/office-expense': { entityKey: 'officeExpense', icon: 'fa-file-invoice-dollar' },
      '/property-expense': { entityKey: 'propertyExpense', icon: 'fa-file-invoice-dollar' },
      '/receipt-voucher': { entityKey: 'receiptVoucher', icon: 'fa-file-invoice' },
      '/paid-voucher': { entityKey: 'paidVoucher', icon: 'fa-file-invoice' },
      '/residential-unit-receipt': { entityKey: 'residentialUnitReceipt', icon: 'fa-home' },
      '/annual-reconciliation': { entityKey: 'annualReconciliation', icon: 'fa-file-contract' },
      '/bank': { entityKey: 'bank', icon: 'fa-university' },
      '/realestatetype': { entityKey: 'realEstateType', icon: 'fa-building' },
      '/realestateusage': { entityKey: 'realEstateUsage', icon: 'fa-building' },
      '/facilities-and-amenities': { entityKey: 'facilitiesAndAmenities', icon: 'fa-star' },
      '/unit-type': { entityKey: 'unitType', icon: 'fa-cube' },
      '/unitusage': { entityKey: 'unitUsage', icon: 'fa-door-open' },
      '/unit-facilities-amenities': { entityKey: 'unitFacilitiesAndAmenities', icon: 'fa-list' },
      '/country': { entityKey: 'country', icon: 'fa-globe' },
      '/area': { entityKey: 'area', icon: 'fa-map-marked-alt' },
      '/city': { entityKey: 'city', icon: 'fa-city' },
      '/district': { entityKey: 'district', icon: 'fa-map' },
      '/organization': { entityKey: 'organization', icon: 'fa-building' },
      '/branch': { entityKey: 'branch', icon: 'fa-code-branch' },
      '/nationality': { entityKey: 'nationality', icon: 'fa-flag' },
      '/identity': { entityKey: 'identity', icon: 'fa-id-card' },
    };
    
    // Check if current path is a list route
    if (listRoutesMap[path]) {
      const routeInfo = listRoutesMap[path];
      return {
        showNavbar: true,
        title: t(`${routeInfo.entityKey}.list.title`),
        icon: routeInfo.icon,
        showBackButton: false,
      };
    }
    
    // Route patterns: /entity/create, /entity/edit/:id, /entity/view/:id
    const routePatterns = [
      { pattern: /^\/([^/]+)\/create$/, action: 'create' },
      { pattern: /^\/([^/]+)\/edit\/\d+$/, action: 'edit' },
      { pattern: /^\/([^/]+)\/view\/\d+$/, action: 'view' },
    ];

    for (const routePattern of routePatterns) {
      const match = path.match(routePattern.pattern);
      if (match) {
        const entity = match[1];
        const action = routePattern.action;
        
        // Map entity names to translation keys (handle special cases)
        const entityMap = {
          'property': 'property',
          'contract': 'contract',
          'brokerage-agreement': 'brokerageAgreement',
          'property-management-income': 'propertyManagementIncome',
          'additional-income-report': 'additionalIncomeReport',
          'office-expense': 'officeExpense',
          'property-expense': 'propertyExpense',
          'receipt-voucher': 'receiptVoucher',
          'paid-voucher': 'paidVoucher',
          'residential-unit-receipt': 'residentialUnitReceipt',
          'annual-reconciliation': 'annualReconciliation',
          'country': 'country',
          'area': 'area',
          'city': 'city',
          'district': 'district',
          'organization': 'organization',
          'branch': 'branch',
          'nationality': 'nationality',
          'identity': 'identity',
          'bank': 'bank',
          'realestatetype': 'realEstateType',
          'realestateusage': 'realEstateUsage',
          'facilities-and-amenities': 'facilitiesAndAmenities',
          'unit-type': 'unitType',
          'unitusage': 'unitUsage',
          'unit-facilities-amenities': 'unitFacilitiesAndAmenities',
        };

        const entityKey = entityMap[entity] || entity;
        const titleKey = `${entityKey}.${action}.title`;
        const backToListKey = `${entityKey}.${action}.backToList`;
        
        // Determine back navigation path
        let backPath = `/${entity}`;
        // Special cases for routes that don't match entity name
        if (entity === 'brokerage-agreement') {
          backPath = '/brokerage-agreement';
        } else if (entity === 'property-management-income') {
          backPath = '/property-management-income';
        } else if (entity === 'additional-income-report') {
          backPath = '/additional-income-report';
        } else if (entity === 'office-expense') {
          backPath = '/office-expense';
        } else if (entity === 'property-expense') {
          backPath = '/property-expense';
        } else if (entity === 'receipt-voucher') {
          backPath = '/receipt-voucher';
        } else if (entity === 'paid-voucher') {
          backPath = '/paid-voucher';
        } else if (entity === 'residential-unit-receipt') {
          backPath = '/residential-unit-receipt';
        } else if (entity === 'annual-reconciliation') {
          backPath = '/annual-reconciliation';
        } else if (entity === 'realestatetype') {
          backPath = '/realestatetype';
        } else if (entity === 'realestateusage') {
          backPath = '/realestateusage';
        } else if (entity === 'facilities-and-amenities') {
          backPath = '/facilities-and-amenities';
        } else if (entity === 'unit-type') {
          backPath = '/unit-type';
        } else if (entity === 'unitusage') {
          backPath = '/unitusage';
        } else if (entity === 'unit-facilities-amenities') {
          backPath = '/unit-facilities-amenities';
        }

        return {
          showNavbar: true,
          title: t(titleKey),
          backPath: backPath,
          backText: t(backToListKey),
        };
      }
    }

    return { showNavbar: false };
  };

  const navbarInfo = getNavbarInfo();

  const toggleNavMode = () => {
    const next = navMode === 'tree' ? 'menu' : 'tree';
    setNavMode(next);
    localStorage.setItem(NAV_MODE_KEY, next);
    // Leaving the tree lands on the dashboard rather than a stale deep route.
    if (next === 'menu') navigate('/dashboard');
  };
  const isRTL = i18n.language === 'ar';
  
  const selectedBranch = branches.find(b => (b.id || b.Id) === selectedBranchId);
  const filteredBranches = branches.filter(b => {
    if (!branchSearchTerm) return true;
    const search = branchSearchTerm.toLowerCase();
    const nameAr = (b.branchNameAr || b.BranchNameAr || '').toLowerCase();
    const nameEng = (b.branchNameEng || b.BranchNameEng || '').toLowerCase();
    const code = (b.branchCode || b.BranchCode || '').toLowerCase();
    return nameAr.includes(search) || nameEng.includes(search) || code.includes(search);
  });

  const treeMode = navMode === 'tree';

  return (
    <div className={`app-container ${treeMode ? 'app-container-tree' : ''}`}>
      {!treeMode && (
      <aside className="sidebar">
        <div className="sidebar-header">
          <img src="/logo.png" alt="Agar Live" className="sidebar-logo" />
          <span className="sidebar-title">{i18n.language === 'ar' ? 'عقار لايف' : 'Agar Live'}</span>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-item">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
              <i className="fas fa-home nav-icon"></i>
              <span>{t('common.dashboard')}</span>
            </Link>
          </div>

          <div className="nav-section">
            <div className="nav-section-header" onClick={() => toggleSection('agarModules')}>
              <i className="fas fa-cog nav-icon"></i>
              <span>{t('nav.agarModules')}</span>
              <i className={`fas fa-chevron-${expandedSections.agarModules ? 'up' : 'down'} nav-arrow`}></i>
            </div>
            <div className={`nav-section-items ${expandedSections.agarModules ? 'expanded' : 'collapsed'}`}>
              <Link to="/property" className={`nav-link ${isActive('/property') ? 'active' : ''}`}>
                <i className="fas fa-building nav-icon"></i>
                <span>{t('nav.property')}</span>
              </Link>
              <Link to="/contract" className={`nav-link ${isActive('/contract') ? 'active' : ''}`}>
                <i className="fas fa-file-contract nav-icon"></i>
                <span>{t('nav.contract')}</span>
              </Link>
              <Link to="/brokerage-agreement" className={`nav-link ${isActive('/brokerage-agreement') ? 'active' : ''}`}>
                <i className="fas fa-handshake nav-icon"></i>
                <span>{t('nav.brokerageAgreement')}</span>
              </Link>
            </div>
          </div>

          <div className="nav-section">
            <div className="nav-section-header" onClick={() => toggleSection('incomeAndExpenses')}>
              <i className="fas fa-money-bill-wave nav-icon"></i>
              <span>{t('nav.incomeAndExpenses')}</span>
              <i className={`fas fa-chevron-${expandedSections.incomeAndExpenses ? 'up' : 'down'} nav-arrow`}></i>
            </div>
            <div className={`nav-section-items ${expandedSections.incomeAndExpenses ? 'expanded' : 'collapsed'}`}>
              <Link to="/property-management-income" className={`nav-link ${isActive('/property-management-income') ? 'active' : ''}`}>
                <i className="fas fa-chart-line nav-icon"></i>
                <span>{t('nav.propertyManagementIncome')}</span>
              </Link>
              <Link to="/additional-income-report" className={`nav-link ${isActive('/additional-income-report') ? 'active' : ''}`}>
                <i className="fas fa-file-invoice-dollar nav-icon"></i>
                <span>{t('nav.additionalIncomeReport')}</span>
              </Link>
              <Link to="/office-expense" className={`nav-link ${isActive('/office-expense') ? 'active' : ''}`}>
                <i className="fas fa-file-invoice-dollar nav-icon"></i>
                <span>{t('nav.officeExpense')}</span>
              </Link>
              <Link to="/property-expense" className={`nav-link ${isActive('/property-expense') ? 'active' : ''}`}>
                <i className="fas fa-file-invoice-dollar nav-icon"></i>
                <span>{t('nav.propertyExpense')}</span>
              </Link>
            </div>
          </div>

          <div className="nav-section">
            <div className="nav-section-header" onClick={() => toggleSection('forms')}>
              <i className="fas fa-file-alt nav-icon"></i>
              <span>{t('nav.forms', 'Forms')}</span>
              <i className={`fas fa-chevron-${expandedSections.forms ? 'up' : 'down'} nav-arrow`}></i>
            </div>
            <div className={`nav-section-items ${expandedSections.forms ? 'expanded' : 'collapsed'}`}>
              <Link to="/receipt-voucher" className={`nav-link ${isActive('/receipt-voucher') ? 'active' : ''}`}>
                <i className="fas fa-file-invoice nav-icon"></i>
                <span>{t('nav.receiptVoucher')}</span>
              </Link>
              <Link to="/paid-voucher" className={`nav-link ${isActive('/paid-voucher') ? 'active' : ''}`}>
                <i className="fas fa-file-invoice nav-icon"></i>
                <span>{t('nav.paidVoucher')}</span>
              </Link>
              <Link to="/residential-unit-receipt" className={`nav-link ${isActive('/residential-unit-receipt') ? 'active' : ''}`}>
                <i className="fas fa-home nav-icon"></i>
                <span>{t('nav.residentialUnitReceipt')}</span>
              </Link>
              <Link to="/annual-reconciliation" className={`nav-link ${isActive('/annual-reconciliation') ? 'active' : ''}`}>
                <i className="fas fa-file-contract nav-icon"></i>
                <span>{t('nav.annualReconciliation')}</span>
              </Link>
            </div>
          </div>

          {isSuperAdmin && (
            <div className="nav-section">
              <div className="nav-section-header" onClick={() => toggleSection('agarConfiguration')}>
                <i className="fas fa-cogs nav-icon"></i>
                <span>{t('nav.agarConfiguration')}</span>
                <i className={`fas fa-chevron-${expandedSections.agarConfiguration ? 'up' : 'down'} nav-arrow`}></i>
              </div>
              <div className={`nav-section-items ${expandedSections.agarConfiguration ? 'expanded' : 'collapsed'}`}>
                <Link to="/bank" className={`nav-link ${isActive('/bank') ? 'active' : ''}`}>
                  <i className="fas fa-university nav-icon"></i>
                  <span>{t('nav.bank')}</span>
                </Link>
                <Link to="/realestatetype" className={`nav-link ${isActive('/realestatetype') ? 'active' : ''}`}>
                  <i className="fas fa-building nav-icon"></i>
                  <span>{t('nav.propertyType')}</span>
                </Link>
                <Link to="/realestateusage" className={`nav-link ${isActive('/realestateusage') ? 'active' : ''}`}>
                  <i className="fas fa-building nav-icon"></i>
                  <span>{t('nav.propertyUsage')}</span>
                </Link>
                <Link to="/facilities-and-amenities" className={`nav-link ${isActive('/facilities-and-amenities') ? 'active' : ''}`}>
                  <i className="fas fa-star nav-icon"></i>
                  <span>{t('nav.facilitiesAndAmenities')}</span>
                </Link>
                <Link to="/unit-type" className={`nav-link ${isActive('/unit-type') ? 'active' : ''}`}>
                  <i className="fas fa-cube nav-icon"></i>
                  <span>{t('nav.unitType')}</span>
                </Link>
                <Link to="/unitusage" className={`nav-link ${isActive('/unitusage') ? 'active' : ''}`}>
                  <i className="fas fa-door-open nav-icon"></i>
                  <span>{t('nav.unitUsage')}</span>
                </Link>
                <Link to="/unit-facilities-amenities" className={`nav-link ${isActive('/unit-facilities-amenities') ? 'active' : ''}`}>
                  <i className="fas fa-list nav-icon"></i>
                  <span>{t('nav.unitFacilities')}</span>
                </Link>
              </div>
            </div>
          )}

          {isSuperAdmin && (
            <div className="nav-section">
              <div className="nav-section-header" onClick={() => toggleSection('systemConfiguration')}>
                <i className="fas fa-sliders-h nav-icon"></i>
                <span>{t('nav.systemConfiguration')}</span>
                <i className={`fas fa-chevron-${expandedSections.systemConfiguration ? 'up' : 'down'} nav-arrow`}></i>
              </div>
              <div className={`nav-section-items ${expandedSections.systemConfiguration ? 'expanded' : 'collapsed'}`}>
                <Link to="/country" className={`nav-link ${isActive('/country') ? 'active' : ''}`}>
                  <i className="fas fa-globe nav-icon"></i>
                  <span>{t('nav.country')}</span>
                </Link>
                <Link to="/area" className={`nav-link ${isActive('/area') ? 'active' : ''}`}>
                  <i className="fas fa-map-marked-alt nav-icon"></i>
                  <span>{t('nav.area')}</span>
                </Link>
                <Link to="/city" className={`nav-link ${isActive('/city') ? 'active' : ''}`}>
                  <i className="fas fa-city nav-icon"></i>
                  <span>{t('nav.city')}</span>
                </Link>
                <Link to="/district" className={`nav-link ${isActive('/district') ? 'active' : ''}`}>
                  <i className="fas fa-map nav-icon"></i>
                  <span>{t('nav.district')}</span>
                </Link>
                <Link to="/organization" className={`nav-link ${isActive('/organization') ? 'active' : ''}`}>
                  <i className="fas fa-building nav-icon"></i>
                  <span>{t('nav.organization')}</span>
                </Link>
                <Link to="/branch" className={`nav-link ${isActive('/branch') ? 'active' : ''}`}>
                  <i className="fas fa-code-branch nav-icon"></i>
                  <span>{t('nav.branch')}</span>
                </Link>
                <Link to="/nationality" className={`nav-link ${isActive('/nationality') ? 'active' : ''}`}>
                  <i className="fas fa-flag nav-icon"></i>
                  <span>{t('nav.nationality')}</span>
                </Link>
                <Link to="/identity" className={`nav-link ${isActive('/identity') ? 'active' : ''}`}>
                  <i className="fas fa-id-card nav-icon"></i>
                  <span>{t('nav.identity')}</span>
                </Link>
              </div>
            </div>
          )}

          {(isSuperAdmin || isOrgAdmin) && (
            <div className="nav-item">
              <button
                type="button"
                className="nav-link nav-link-button"
                onClick={() => setShowOfficeSettings(true)}
              >
                <i className="fas fa-sliders nav-icon"></i>
                <span>{t('settings.title')}</span>
              </button>
            </div>
          )}
        </nav>
      </aside>
      )}
      <main className="main-content">
        {/* Navbar with Avatar */}
        <nav className="top-navbar">
          <div className="navbar-content">
            {navbarInfo.showNavbar && (
              <div className="navbar-left">
                {navbarInfo.showBackButton !== false && navbarInfo.backPath && (
                  <button 
                    className="navbar-back-btn" 
                    onClick={() => navigate(navbarInfo.backPath)}
                    title={navbarInfo.backText}
                  >
                    <i className={`fas fa-arrow-${i18n.language === 'ar' ? 'right' : 'left'}`}></i>
                  </button>
                )}
                <div className="navbar-title">
                  {navbarInfo.icon && <i className={`fas ${navbarInfo.icon}`}></i>}
                  <span>{navbarInfo.title}</span>
                </div>
              </div>
            )}
            {!navbarInfo.showNavbar && <div className="navbar-spacer"></div>}
            
            <button
              type="button"
              className={`navbar-mode-toggle ${treeMode ? 'navbar-mode-toggle-on' : ''}`}
              onClick={toggleNavMode}
              title={t(treeMode ? 'nav.switchToMenu' : 'nav.switchToTree')}
              aria-label={t(treeMode ? 'nav.switchToMenu' : 'nav.switchToTree')}
            >
              <i className={`fas ${treeMode ? 'fa-bars' : 'fa-sitemap'}`}></i>
            </button>

            {/* Branch Selector - Hide for branch users, show for others */}
            {!(user && (user.branchId || user.BranchId)) && (
              <div className="navbar-branch-selector" ref={branchSelectRef}>
                <button
                  className="navbar-branch-btn"
                  onClick={() => setShowBranchSelector(!showBranchSelector)}
                  title={t('common.selectBranch', 'Select Branch')}
                >
                  <i className="fas fa-code-branch"></i>
                  <span className="branch-selector-text">
                    {selectedBranchId === null 
                      ? t('common.allBranches', 'All Branches')
                      : (selectedBranch ? (isRTL ? (selectedBranch.branchNameAr || selectedBranch.BranchNameAr || selectedBranch.branchNameEng || selectedBranch.BranchNameEng) : (selectedBranch.branchNameEng || selectedBranch.BranchNameEng || selectedBranch.branchNameAr || selectedBranch.BranchNameAr)) : t('common.selectBranch', 'Select Branch'))
                    }
                  </span>
                  <i className={`fas fa-chevron-${showBranchSelector ? 'up' : 'down'}`}></i>
                </button>
                {showBranchSelector && (
                  <div className="navbar-branch-dropdown">
                    <div className="navbar-branch-search">
                      <i className="fas fa-search"></i>
                      <input
                        type="text"
                        placeholder={t('common.search', 'Search')}
                        value={branchSearchTerm}
                        onChange={(e) => setBranchSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="navbar-branch-options">
                      <div
                        className={`navbar-branch-option ${selectedBranchId === null ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedBranchId(null);
                          setShowBranchSelector(false);
                          setBranchSearchTerm('');
                        }}
                      >
                        <i className="fas fa-globe"></i>
                        <span>{t('common.allBranches', 'All Branches')}</span>
                      </div>
                      {filteredBranches.length === 0 ? (
                        <div className="navbar-branch-no-results">{t('common.noData', 'No data')}</div>
                      ) : (
                        filteredBranches.map(branch => (
                          <div
                            key={branch.id || branch.Id}
                            className={`navbar-branch-option ${(branch.id || branch.Id) === selectedBranchId ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedBranchId(branch.id || branch.Id);
                              setShowBranchSelector(false);
                              setBranchSearchTerm('');
                            }}
                          >
                            <i className="fas fa-building"></i>
                            <span>
                              {isRTL ? (branch.branchNameAr || branch.BranchNameAr || branch.branchNameEng || branch.BranchNameEng) : (branch.branchNameEng || branch.BranchNameEng || branch.branchNameAr || branch.BranchNameAr)}
                              {(branch.branchCode || branch.BranchCode) && ` (${branch.branchCode || branch.BranchCode})`}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Show current branch for branch users (read-only) */}
            {user && (user.branchId || user.BranchId) && selectedBranch && (
              <div className="navbar-branch-selector">
                <div className="navbar-branch-btn" style={{ cursor: 'default', opacity: 0.8 }}>
                  <i className="fas fa-code-branch"></i>
                  <span className="branch-selector-text">
                    {isRTL ? (selectedBranch.branchNameAr || selectedBranch.BranchNameAr || selectedBranch.branchNameEng || selectedBranch.BranchNameEng) : (selectedBranch.branchNameEng || selectedBranch.BranchNameEng || selectedBranch.branchNameAr || selectedBranch.BranchNameAr)}
                    {(selectedBranch.branchCode || selectedBranch.BranchCode) && ` (${selectedBranch.branchCode || selectedBranch.BranchCode})`}
                  </span>
                </div>
              </div>
            )}

            <div className="avatar-dropdown-container">
              <button
                className="avatar-button"
                onClick={() => setShowAvatarDropdown(!showAvatarDropdown)}
                aria-label="User menu"
              >
                <div className="avatar-circle">
                <i className="fas fa-user"></i>
              </div>
                {user && (
                  <div className="avatar-info">
                    <div className="avatar-name">{user.fullName || user.userName}</div>
                {user.isSuperAdmin && (
                      <div className="avatar-role">
                        <i className="fas fa-crown"></i>
                        {t('common.superAdmin', 'Super Admin')}
                      </div>
                    )}
                  </div>
                )}
                <i className={`fas fa-chevron-${showAvatarDropdown ? 'up' : 'down'} avatar-chevron`}></i>
              </button>
              
              {showAvatarDropdown && (
                <div className="avatar-dropdown">
                  {user && (
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar-circle">
                        <i className="fas fa-user"></i>
                      </div>
                      <div className="dropdown-user-details">
                        <div className="dropdown-user-name">{user.fullName || user.userName}</div>
                        {user.isSuperAdmin && (
                          <div className="dropdown-user-role">
                            <i className="fas fa-crown"></i>
                            {t('common.superAdmin', 'Super Admin')}
                          </div>
                        )}
                        {user.email && (
                          <div className="dropdown-user-email">{user.email}</div>
                )}
              </div>
            </div>
          )}
                  
                  <div className="dropdown-divider"></div>
                  
                  <div className="dropdown-section">
                    <div className="dropdown-label">{t('common.language', 'Language')}</div>
                    <div className="language-switcher-dropdown">
                      <button
                        type="button"
                        className={`lang-dropdown-btn ${i18n.language === 'en' ? 'active' : ''}`}
                        onClick={() => changeLanguage('en')}
                      >
                        <i className="fas fa-globe"></i>
                        <span>English</span>
                        {i18n.language === 'en' && <i className="fas fa-check"></i>}
                      </button>
                      <button
                        type="button"
                        className={`lang-dropdown-btn ${i18n.language === 'ar' ? 'active' : ''}`}
                        onClick={() => changeLanguage('ar')}
                      >
                        <i className="fas fa-globe"></i>
                        <span>العربية</span>
                        {i18n.language === 'ar' && <i className="fas fa-check"></i>}
                      </button>
                    </div>
                  </div>
                  
                  {(isSuperAdmin || isOrgAdmin) && (
                    <>
                      <div className="dropdown-divider"></div>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setShowAvatarDropdown(false);
                          setShowOfficeSettings(true);
                        }}
                      >
                        <i className="fas fa-sliders"></i>
                        <span>{t('settings.title')}</span>
                      </button>
                    </>
                  )}

                  <div className="dropdown-divider"></div>
                  
          <button 
                    className="dropdown-item logout-item"
                    onClick={handleLogout}
          >
            <i className="fas fa-sign-out-alt"></i>
                    <span>{t('common.logout', 'Logout')}</span>
          </button>
                </div>
              )}
            </div>
          </div>
        </nav>
        <div className="content-wrapper">
          {treeMode ? <TreeBrowser /> : children}
        </div>
      </main>

      <OfficeSettingsModal
        show={showOfficeSettings}
        onClose={() => setShowOfficeSettings(false)}
      />
    </div>
  );
};

export default Layout;
